/** Типы ответов API — общие для сервера и мини-приложения. */
import { z } from 'zod';
import type { Permission, Role } from './roles.ts';
import { ROLES } from './roles.ts';

export type EmployeeStatus = 'active' | 'blocked';

/**
 * Сотрудник из списка. Войти в приложение может только тот, кто в списке:
 * администратор указывает Telegram ID, @username или телефон, и при первом входе запись привязывается к Telegram.
 */
export interface EmployeeDto {
  id: string;
  /** Telegram ID; null — ещё не входил и задан только @username или телефон. */
  userId: number | null;
  fullName: string;
  position: string | null;
  role: Role;
  status: EmployeeStatus;
  areaIds: string[];
  phone: string | null;
  /** @username из списка (без @) или текущий из Telegram после входа. */
  tgUsername: string | null;
  /** Последний вход в приложение; null — ещё не входил. */
  lastSeenAt: string | null;
  createdAt: string;
}

/** Кто открывал приложение, но его нет в списке. Администратор может добавить его одной кнопкой. */
export interface LoginAttemptDto {
  userId: number;
  firstName: string;
  lastName: string | null;
  username: string | null;
  lastSeenAt: string;
}

export interface MeDto {
  user: { id: number; firstName: string; lastName: string | null; username: string | null };
  /** null — человека нет в списке сотрудников: вход закрыт. */
  employee: EmployeeDto | null;
  permissions: Permission[];
  /** Для администратора: сколько людей не из списка пытались войти за неделю. */
  loginAttempts: number;
}

export type AreaKind = 'site' | 'shop' | 'line' | 'cell';
export const AREA_KIND_LABEL: Record<AreaKind, string> = { site: 'Площадка', shop: 'Цех', line: 'Линия', cell: 'Участок' };

export interface AreaDto {
  id: string;
  parentId: string | null;
  name: string;
  kind: AreaKind;
  sort: number;
}

/** Состояние связи с панелью по последней проверке. */
export interface PanelLink {
  ok: boolean | null;
  checkedAt: string | null;
  rttMs: number | null;
  error: string | null;
}

export interface PanelDto {
  id: string;
  areaId: string | null;
  name: string;
  description: string | null;
  /** Можно ли этому пользователю управлять (право роли и флаг панели). */
  canControl: boolean;
  /** Разрешено ли управление на самой панели (настройка администратора). */
  controlAllowed: boolean;
  enabled: boolean;
  sort: number;
  link: PanelLink;
}

/** Для администратора: адрес и признак пароля. Сам пароль наружу не отдаётся никогда. */
export interface PanelAdminDto extends PanelDto {
  host: string;
  port: number;
  hasPassword: boolean;
}

export type VncMode = 'view' | 'control';

export interface VncTicketDto {
  ticket: string;
  /** Номер сеанса в журнале — к нему приложение прикладывает снимки экрана в момент нажатий. */
  sessionId: string;
  /** Путь WebSocket относительно сайта: /vnc/<ticket>. */
  path: string;
  mode: VncMode;
  expiresInSec: number;
}

export interface PanelCheckDto {
  ok: boolean;
  /** Что получилось: «связь есть, RFB 3.8, пароль принят» или текст ошибки. */
  message: string;
  rfbVersion: string | null;
  security: string | null;
  width: number | null;
  height: number | null;
  desktopName: string | null;
  rttMs: number | null;
}

export interface VncSessionDto {
  id: string;
  panelId: string;
  panelName: string;
  employeeId: string;
  employeeName: string;
  mode: VncMode;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  bytesDown: number;
  bytesUp: number;
  /** Сеанс идёт прямо сейчас (его можно оборвать). */
  live: boolean;
  clicks: number;
  keys: number;
  shots: number;
}

/** Действие в сеансе, записанное сервером из потока команд (подделать с телефона нельзя). */
export interface VncEventDto {
  at: string;
  kind: 'press' | 'drag' | 'key' | 'paste';
  /** Координаты на экране панели (для press — точка нажатия, для drag — где отпустили). */
  x: number | null;
  y: number | null;
  /** Для drag — откуда тянули. */
  fromX: number | null;
  fromY: number | null;
  /** Для key — название клавиши или символ; для paste — сколько символов вставлено. */
  text: string | null;
}

/** Снимок экрана в момент нажатия (делает приложение; точка нажатия отмечена на картинке). */
export interface VncShotDto {
  id: string;
  at: string;
}

export interface VncSessionDetailDto extends VncSessionDto {
  clientIp: string | null;
  screen: { width: number; height: number } | null;
  events: VncEventDto[];
  shotList: VncShotDto[];
}

/* ---------- Проверка входных данных (одна схема на форму и сервер) ---------- */

/** Telegram ID (число) или @username. */
const TgRef = z
  .string()
  .trim()
  .regex(/^(@?[A-Za-z][A-Za-z0-9_]{3,31}|\d{1,15})$/, 'Telegram: числовой ID (например 1014614490) или @username')
  .transform((v) => v.replace(/^@/, ''));

/** Телефон: цифры, приводятся к виду 7XXXXXXXXXX для российских номеров. */
export function normalizePhone(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('8')) d = `7${d.slice(1)}`;
  if (d.length === 10) d = `7${d}`;
  return d.length >= 10 && d.length <= 15 ? d : null;
}

const Phone = z
  .string()
  .trim()
  .refine((v) => normalizePhone(v) !== null, 'Телефон: 10–15 цифр, например +7 916 123-45-67')
  .transform((v) => normalizePhone(v)!);

export const EmployeeInput = z
  .object({
    fullName: z.string().trim().min(3, 'Укажите фамилию и имя').max(120),
    position: z.string().trim().max(120).nullable().optional(),
    role: z.enum(ROLES),
    areaIds: z.array(z.string().uuid()).max(200).default([]),
    telegram: TgRef.nullable().optional(),
    phone: Phone.nullable().optional(),
  })
  .refine((v) => v.telegram || v.phone, { message: 'Укажите Telegram (ID или @username) или телефон — по ним сотрудник войдёт', path: ['telegram'] });
export type EmployeeInput = z.input<typeof EmployeeInput>;

export const EmployeePatch = z.object({
  fullName: z.string().trim().min(3).max(120).optional(),
  position: z.string().trim().max(120).nullable().optional(),
  role: z.enum(ROLES).optional(),
  status: z.enum(['active', 'blocked']).optional(),
  areaIds: z.array(z.string().uuid()).max(200).optional(),
  /** Менять можно, пока сотрудник ни разу не входил. */
  telegram: TgRef.nullable().optional(),
  phone: Phone.nullable().optional(),
});
export type EmployeePatch = z.input<typeof EmployeePatch>;

export const AreaInput = z.object({
  name: z.string().trim().min(1, 'Название не может быть пустым').max(80),
  kind: z.enum(['site', 'shop', 'line', 'cell']).default('cell'),
  parentId: z.string().uuid().nullable().optional(),
  sort: z.number().int().min(0).max(10000).optional(),
});
export type AreaInput = z.input<typeof AreaInput>;

/** Адрес: IP или имя хоста в сети завода. Без схемы, пробелов и пути. */
const HOST_RE = /^[a-zA-Z0-9]([a-zA-Z0-9.-]{0,251}[a-zA-Z0-9])?$/;

export const PanelInput = z.object({
  name: z.string().trim().min(1, 'Название не может быть пустым').max(80),
  description: z.string().trim().max(300).nullable().optional(),
  areaId: z.string().uuid().nullable().optional(),
  host: z.string().trim().regex(HOST_RE, 'Адрес — IP или имя компьютера, например 192.168.1.20'),
  port: z.number().int().min(1, 'Порт от 1 до 65535').max(65535, 'Порт от 1 до 65535'),
  /** Строка — задать пароль, null — убрать, не передавать — оставить как есть. */
  password: z.string().max(64).nullable().optional(),
  controlAllowed: z.boolean().optional(),
  enabled: z.boolean().optional(),
  sort: z.number().int().min(0).max(10000).optional(),
});
export type PanelInput = z.input<typeof PanelInput>;
